import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ClosedownFile,
  ClosedownFileType,
  ClosedownJobDetail,
  ClosedownOption,
  ClosedownPayload,
  ClosedownStatus,
  ClosedownUploadFile,
  PurchaseOrder,
  RootCause,
  StatusAffirmation
} from '@app/models/closedown-detail.model';
import { InvoiceService } from '@app/services/invoice/invoice.service';
import { JobService } from '@app/services/job/job.service';
import { NavigationService } from '@app/services/navigation/navigation.service';
import { firstValueFrom, forkJoin, of, switchMap } from 'rxjs';

type DurationControlName = 'maintenanceTime' | 'travelTime';
type DurationPart = 'hours' | 'minutes';

@Component({
  selector: 'app-open-job-detail',
  templateUrl: './open-job-detail.component.html',
  styleUrls: ['./open-job-detail.component.scss'],
  standalone: false
})
export class OpenJobDetailComponent implements OnInit, OnDestroy {
  private durationRepeatDelay?: ReturnType<typeof setTimeout>;
  private durationRepeatInterval?: ReturnType<typeof setInterval>;

  readonly closedownForm = this.formBuilder.group({
    assetTag: ['', [Validators.minLength(2), Validators.maxLength(8)]],
    rootCause: [null as string | null, Validators.required],
    gasUsed: [false],
    gasType: [null as number | null],
    gasUsage: [0, Validators.min(0)],
    gasLeakType: [null as number | null],
    status: [null as string | null, Validators.required],
    statusSubType: [null as string | null, Validators.required],
    notes: [''],
    onsiteDate: [null as Date | null, Validators.required],
    onsiteTime: ['', Validators.required],
    maintenanceTime: ['00:00', [Validators.required, Validators.pattern(/^(?:[01]\d|2[0-3]):[0-5]\d$/)]],
    travelTime: ['00:00', [Validators.required, Validators.pattern(/^(?:[01]\d|2[0-3]):[0-5]\d$/)]],
    returnDate: [null as Date | null],
    returnTime: ['']
  });

  job?: ClosedownJobDetail;
  rootCauses: RootCause[] = [];
  statuses: ClosedownStatus[] = [];
  affirmations: StatusAffirmation[] = [];
  gasTypes: ClosedownOption[] = [];
  gasLeakTypes: ClosedownOption[] = [];
  purchaseOrder: PurchaseOrder | null = null;
  photoReasons: string[] = [];
  files: ClosedownFile[] = [];
  siteId = '';
  siteName = '';
  jobType = '';
  jobId = '';
  isSaving = false;
  hasLoadError = false;
  loadErrorKey = '';
  saveErrorKey = '';
  saveSucceeded = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    private readonly jobService: JobService,
    private readonly invoiceService: InvoiceService,
    private readonly navigationService: NavigationService
  ) {
    this.navigationService.onChange(true, false);
  }

  get showGasFields(): boolean {
    return this.closedownForm.controls.gasUsed.value ?? false;
  }

  get showReturnEta(): boolean {
    const statusId = this.closedownForm.controls.status.value;
    const statusName = this.statuses.find(status => status.id === statusId)?.name
      ?.replaceAll('-', '').replaceAll(' ', '').toUpperCase();
    return statusName === 'RETURNING' || statusName === 'AWAITINGPARTS';
  }

  get photos(): ClosedownFile[] {
    return this.files.filter(file => file.type === 'photo');
  }

  get supportingDocuments(): ClosedownFile[] {
    return this.files.filter(file => file.type === 'supporting-document');
  }

  get serviceSheets(): ClosedownFile[] {
    return this.files.filter(file => file.type === 'service-sheet');
  }

  get canCreateUplift(): boolean {
    return !this.purchaseOrder
      || (this.purchaseOrder.purchaseOrderLines.length === 0
        && this.purchaseOrder.upliftRequests.length === 0);
  }

  getDurationPart(controlName: DurationControlName, part: DurationPart): string {
    const [hours = '00', minutes = '00'] = this.closedownForm.controls[controlName].value?.split(':') || [];
    return part === 'hours' ? hours : minutes;
  }

  adjustDuration(controlName: DurationControlName, part: DurationPart, change: number): void {
    const current = Number(this.getDurationPart(controlName, part));
    this.setDurationPart(controlName, part, current + change);
  }

  startDurationAdjustment(
    event: PointerEvent,
    controlName: DurationControlName,
    part: DurationPart,
    change: number
  ): void {
    event.preventDefault();
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    this.stopDurationAdjustment();
    this.adjustDuration(controlName, part, change);
    this.durationRepeatDelay = setTimeout(() => {
      this.durationRepeatInterval = setInterval(
        () => this.adjustDuration(controlName, part, change),
        100
      );
    }, 400);
  }

  stopDurationAdjustment(): void {
    if (this.durationRepeatDelay) {
      clearTimeout(this.durationRepeatDelay);
      this.durationRepeatDelay = undefined;
    }

    if (this.durationRepeatInterval) {
      clearInterval(this.durationRepeatInterval);
      this.durationRepeatInterval = undefined;
    }
  }

  updateDurationPart(controlName: DurationControlName, part: DurationPart, event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    this.setDurationPart(controlName, part, value);
  }

  ngOnInit(): void {
    this.siteId = this.route.snapshot.paramMap.get('siteId') || '';
    this.jobType = this.route.snapshot.paramMap.get('jobType') || '';
    this.jobId = this.route.snapshot.paramMap.get('jobId') || '';
    this.siteName = this.route.snapshot.queryParamMap.get('siteName') || '';

    if (!this.siteId || !this.jobType || !this.jobId) {
      this.setLoadError('open_job_detail_error_missing_details');
      return;
    }

    this.closedownForm.controls.status.valueChanges.pipe(
      switchMap(statusId => statusId === null
        ? of([] as StatusAffirmation[])
        : this.jobService.getAffirmations(statusId))
    ).subscribe({
      next: affirmations => {
        this.affirmations = affirmations;
        this.closedownForm.controls.statusSubType.setValue(affirmations[0]?.id ?? null);
        this.updateReturnEtaValidation();
      },
      error: () => {
        this.affirmations = [];
        this.closedownForm.controls.statusSubType.setValue(null);
        this.saveErrorKey = 'open_job_detail_error_status_options';
      }
    });

    this.jobService.getJob(this.jobId).pipe(
      switchMap(job => forkJoin({
        job: of(job),
        rootCauses: this.jobService.getRootCauses(
          job.brandId || job.site?.brand?.id || '',
          job.disciplineReferenceId || '',
          job.faultServiceReferenceId || '',
          job.incidentTypeReferenceId || ''
        ),
        statuses: this.jobService.getStatuses(),
        purchaseOrder: this.invoiceService.getUpliftPurchaseOrder(this.jobId, job.companyId),
        //gasTypes: this.jobService.getGasTypes(),
        //photoReasons: this.jobService.getPhotoReasons()
      }))
    ).subscribe({
      next: result => {
        this.job = result.job;
        this.siteName = this.siteName || result.job.site?.name || '';
        this.rootCauses = result.rootCauses || [];
        this.statuses = result.statuses || [];
        //this.gasTypes = result.gasTypes || [];
        this.purchaseOrder = result.purchaseOrder;
        //this.photoReasons = result.photoReasons || [];
        this.closedownForm.patchValue({ assetTag: result.job.asset?.assetTag || '' });
        this.setOnsiteToNow();
        this.loadGasLeakTypes(result.job.disciplineId);
      },
      error: () => this.setLoadError('open_job_detail_error_load_details')
    });
  }

  ngOnDestroy(): void {
    this.stopDurationAdjustment();
  }

  setOnsiteToNow(): void {
    const now = new Date();
    this.closedownForm.patchValue({
      onsiteDate: now,
      onsiteTime: this.formatTime(now)
    });
  }

  selectFiles(event: Event, type: ClosedownFileType): void {
    const input = event.target as HTMLInputElement;

    for (const file of Array.from(input.files || [])) {
      this.files.push({
        file,
        type,
        reason: type === 'photo' ? this.photoReasons[0] || '' : undefined
      });
    }

    input.value = '';
  }

  removeFile(fileToRemove: ClosedownFile): void {
    this.files = this.files.filter(file => file !== fileToRemove);
  }

  goBack(): void {
    void this.router.navigate(
      ['openjobs', this.siteId, this.jobType],
      { queryParams: { siteName: this.siteName } }
    );
  }

  openUpgrade(): void {
    void this.router.navigate(
      ['openjobs', this.siteId, this.jobType, this.jobId, 'upgrade'],
      { queryParams: { siteName: this.siteName } }
    );
  }

  async saveClosedown(): Promise<void> {
    this.saveErrorKey = '';
    this.saveSucceeded = false;
    this.closedownForm.markAllAsTouched();

    if (this.closedownForm.invalid || !this.job) {
      return;
    }

    const onsite = this.toDate(
      this.closedownForm.controls.onsiteDate.value!,
      this.closedownForm.controls.onsiteTime.value!
    );
    const returnEta = this.showReturnEta
      ? this.toDate(
        this.closedownForm.controls.returnDate.value!,
        this.closedownForm.controls.returnTime.value!
      )
      : undefined;

    if (!onsite || this.addDuration(onsite, this.closedownForm.controls.maintenanceTime.value!) > new Date()) {
      this.saveErrorKey = 'open_job_detail_error_future_job_time';
      return;
    }

    if (this.showReturnEta && (!returnEta || returnEta <= new Date())) {
      this.saveErrorKey = 'open_job_detail_error_future_eta';
      return;
    }

    if (this.photos.some(photo => !photo.reason)) {
      this.saveErrorKey = 'open_job_detail_error_photo_reason';
      return;
    }

    if (this.job.storeId === undefined || this.job.storeTypeId === undefined) {
      this.saveErrorKey = 'open_job_detail_error_store_details';
      return;
    }

    this.isSaving = true;

    try {
      if (this.files.length > 0) {
        const files = await Promise.all(this.files.map(file => this.toUploadFile(file)));
        await firstValueFrom(this.jobService.uploadFiles({
          storeTypeId: this.job.storeTypeId,
          fileDirectory: `JobEvent-${this.jobId}`,
          files,
          siteId: this.job.storeId
        }));
      }

      await firstValueFrom(this.jobService.saveClosedown(this.jobId, this.buildPayload(onsite, returnEta)));
      this.saveSucceeded = true;
    } catch {
      this.saveErrorKey = 'open_job_detail_error_save';
    } finally {
      this.isSaving = false;
    }
  }

  private loadGasLeakTypes(disciplineId?: number): void {
    if (!disciplineId) {
      return;
    }

    this.jobService.getGasLeakTypes(disciplineId).subscribe({
      next: options => this.gasLeakTypes = options,
      error: () => this.gasLeakTypes = []
    });
  }

  private setDurationPart(controlName: DurationControlName, part: DurationPart, value: number): void {
    const control = this.closedownForm.controls[controlName];
    const [currentHours = '00', currentMinutes = '00'] = control.value?.split(':') || [];
    const maximum = part === 'hours' ? 23 : 59;
    const boundedValue = Math.min(maximum, Math.max(0, Number.isFinite(value) ? value : 0));
    const hours = part === 'hours' ? boundedValue : Number(currentHours);
    const minutes = part === 'minutes' ? boundedValue : Number(currentMinutes);

    control.setValue(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`);
    control.markAsDirty();
    control.markAsTouched();
  }

  private updateReturnEtaValidation(): void {
    const returnDate = this.closedownForm.controls.returnDate;
    const returnTime = this.closedownForm.controls.returnTime;

    if (this.showReturnEta) {
      returnDate.addValidators(Validators.required);
      returnTime.addValidators(Validators.required);
    } else {
      returnDate.clearValidators();
      returnTime.clearValidators();
      returnDate.setValue(null);
      returnTime.setValue('');
    }

    returnDate.updateValueAndValidity({ emitEvent: false });
    returnTime.updateValueAndValidity({ emitEvent: false });
  }

  private buildPayload(onsite: Date, returnEta?: Date): ClosedownPayload {
    const values = this.closedownForm.getRawValue();
    const assetTagChanged = values.assetTag !== (this.job?.asset?.assetTag || '');

    return {
      jobType: this.jobType,
      faultService: this.job?.faultService || this.job?.faultServiceName || '',
      storeId: this.job!.storeId!,
      thirdParty: false,
      thirdPartyReason: '',
      gasUsed: values.gasUsed ?? false,
      gasType: values.gasUsed ? values.gasType : null,
      gasUsage: values.gasUsed ? values.gasUsage || 0 : 0,
      gasLeakType: values.gasUsed ? values.gasLeakType : null,
      rootCause: values.rootCause!,
      status: values.status!,
      statusSubType: values.statusSubType!,
      notes: values.notes || '',
      onsite: onsite.toISOString(),
      onsiteUtc: onsite.toISOString(),
      maintenanceTime: values.maintenanceTime!,
      travelTime: values.travelTime!,
      ...(returnEta ? { newETA: returnEta.toISOString() } : {}),
      ...(assetTagChanged ? {
        assetId: this.job?.asset?.assetId || 0,
        unknownAssetTag: values.assetTag || ''
      } : {})
    };
  }

  private async toUploadFile(item: ClosedownFile): Promise<ClosedownUploadFile> {
    return {
      data: await this.readFile(item.file),
      fileName: item.file.name,
      parentType: 'JobEvent',
      parentId: this.jobId,
      fileUploadTypeCode: item.type === 'photo' ? 'UD' : item.type === 'supporting-document' ? 'SD' : 'SS',
      mimeType: item.file.type,
      ...(item.reason ? { note: item.reason } : {})
    };
  }

  private readFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || '').split(',').pop() || '');
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private toDate(date: Date | null, time: string): Date | undefined {
    if (!date || !time) {
      return undefined;
    }

    const [hours, minutes] = time.split(':').map(Number);
    const value = new Date(date);
    value.setHours(hours, minutes, 0, 0);
    return Number.isNaN(value.getTime()) ? undefined : value;
  }

  private addDuration(start: Date, duration: string): Date {
    const [hours, minutes] = duration.split(':').map(Number);
    return new Date(start.getTime() + ((hours * 60) + minutes) * 60_000);
  }

  private formatTime(value: Date): string {
    return `${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}`;
  }

  private setLoadError(messageKey: string): void {
    this.hasLoadError = true;
    this.loadErrorKey = messageKey;
  }
}